import { HttpContext, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, throwError } from 'rxjs';
import { HttpStateService } from '../services/http-state.service';

export const HTTP_SILENT_ERROR = new HttpContextToken<boolean>(() => false);

export function withSilentHttpError(): HttpContext {
  return new HttpContext().set(HTTP_SILENT_ERROR, true);
}

export const httpStateInterceptor: HttpInterceptorFn = (req, next) => {
  const httpState = inject(HttpStateService);
  const isSilentError = req.context.get(HTTP_SILENT_ERROR);

  httpState.startRequest();
  if (!isSilentError) {
    httpState.clearError();
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (isSilentError) {
        return throwError(() => error);
      }

      if (error instanceof HttpErrorResponse) {
        const payload = getPayload(error);
        httpState.setError({
          status: error.status,
          message: parseErrorMessage(error),
          url: req.url,
          method: req.method,
          modulo: resolveModulo(req.url),
          codigo: resolveErrorCode(payload),
          traceId: resolveTraceId(error, payload),
          timestamp: new Date().toISOString(),
        });
      } else {
        httpState.setError({
          status: 0,
          message: 'Error no controlado en solicitud HTTP.',
          url: req.url,
          method: req.method,
          modulo: resolveModulo(req.url),
          timestamp: new Date().toISOString(),
        });
      }

      return throwError(() => error);
    }),
    finalize(() => {
      httpState.finishRequest();
    }),
  );
};

function parseErrorMessage(error: HttpErrorResponse): string {
  const payload = getPayload(error);

  const explicitMessage =
    pickString(payload, ['message', 'mensaje', 'error', 'detail', 'detalle', 'title']) || '';
  if (explicitMessage) {
    return explicitMessage;
  }

  if (typeof error.error === 'string' && error.error.trim()) {
    return error.error;
  }

  switch (error.status) {
    case 0:
      return 'No fue posible conectar con el servidor.';
    case 400:
      return 'Solicitud inválida. Revisa los datos ingresados.';
    case 401:
      return 'Tu sesión no es válida. Debes iniciar sesión nuevamente.';
    case 403:
      return 'No tienes permisos para ejecutar esta acción.';
    case 404:
      return 'No se encontró el recurso solicitado.';
    case 422:
      return 'La solicitud no cumple las validaciones requeridas.';
    case 500:
      return 'Error interno en el servidor.';
    case 503:
      return 'El servicio no está disponible temporalmente.';
    default:
      break;
  }

  if (error.message) {
    return error.message;
  }

  return 'No fue posible completar la solicitud.';
}

function resolveModulo(url: string): string {
  const safeUrl = (url || '').toLowerCase();
  if (!safeUrl) return 'general';

  if (safeUrl.includes('/calendario')) return 'calendario';
  if (safeUrl.includes('/postulaciones')) return 'postulaciones';
  if (safeUrl.includes('/concursos')) return 'concursos';
  if (safeUrl.includes('/perfil')) return 'perfil';
  if (safeUrl.includes('/cv')) return 'cv';
  if (safeUrl.includes('/novedades') || safeUrl.includes('/actualizaciones')) return 'novedades';

  return 'general';
}

function resolveErrorCode(payload: Record<string, unknown>): string | undefined {
  return pickString(payload, ['code', 'error_code', 'errorCode', 'codigo', 'codigo_error']) || undefined;
}

function resolveTraceId(error: HttpErrorResponse, payload: Record<string, unknown>): string | undefined {
  const headerTraceId =
    error.headers.get('x-request-id') ||
    error.headers.get('x-correlation-id') ||
    error.headers.get('trace-id') ||
    error.headers.get('x-trace-id');

  if (headerTraceId) {
    return headerTraceId;
  }

  return (
    pickString(payload, ['traceId', 'trace_id', 'requestId', 'request_id', 'correlationId', 'correlation_id']) ||
    undefined
  );
}

function getPayload(error: HttpErrorResponse): Record<string, unknown> {
  return error.error && typeof error.error === 'object' ? (error.error as Record<string, unknown>) : {};
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}
